import { useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState, type ReactNode } from 'react';
import {
  cabinetApi,
  formatDate,
  type CharacterRow,
  type FormHistoryItem,
  type MyCharacterResponse,
} from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { usePagedLoader } from '../../api/paging';
import { CHARACTER_KIND_LABELS, DORM_LABELS, STUDY_CLASS_LABELS } from '../../api/types';
import { useCountersChanged } from '../../app/counters';
import { t } from '../../i18n';
import {
  BoltIcon,
  BoxIcon,
  DiffIcon,
  EyeIcon,
  HistoryIcon,
  PawIcon,
  PencilIcon,
  VerdictIcon,
  WarningIcon,
} from '../../shared/icons';
import { docNumber } from '../../shared/lib/docNumber';
import {
  Action,
  ActionRow,
  Card,
  CARD_TONE,
  CardBanner,
  CardBody,
  CardHeading,
  CardList,
  ChipRow,
  Divider,
  ErrorState,
  Gap,
  LoadingState,
  Meta,
  NavCard,
  NavList,
  Notice,
  NoticeBlock,
  Prose,
  Screen,
  Section,
  SectionTitle,
  StatusBadge,
  Tag,
  toast,
  type StatusTone,
} from '../../shared/ui';

/**
 * Хаб «Мой персонаж».
 *
 * Главная задача экрана — чтобы человек за секунду понял две вещи: в каком
 * состоянии его анкета и что делать дальше. Поэтому состояние стоит значком в
 * начале карточки — его видно раньше, чем прочитан текст, — а под пояснением
 * ровно одна очевидная кнопка. Переход внутри приложения — тоже кнопка, а не
 * подсвеченный текст, и её вес говорит, чего от игрока ждут: `size="l"` — то,
 * ради чего он сюда пришёл, `size="m" mode="secondary"` — уйти посмотреть на
 * другой экран, `size="m" mode="tertiary"` — раскрыть что-то на месте.
 *
 * Досье, общежитие и класс стоят верхней строкой ТОЙ ЖЕ карточки: это про
 * персонажа, а анкета — про его текст, и отдельным разделом внизу экрана они
 * читались как ещё один список. Почему их не правят, объясняет редактор, где
 * этих пунктов и не хватает.
 *
 * Причина отказа лежит не в /characters/me: отклонённая версия перестаёт быть
 * открытой, и formStatus снова говорит 'none' (или 'approved', если в реестре
 * висит прошлая принятая версия). Комментарий модератора берём из истории —
 * без него отказ выглядит как молчаливая пропажа анкеты.
 */

interface HubData {
  me: MyCharacterResponse;
  /** Непрочитанные решения: их считает аккаунт, а не персонаж. */
  unreadDecisions: number;
}

export function MyCharacterPanel({ id }: NavIdProps) {
  const routeNavigator = useRouteNavigator();
  const [busy, setBusy] = useState(false);

  // Оба чтения помнят себя: на хаб возвращаются из редактора, и прокрутка
  // встаёт по первому рендеру. История нужна там же — по ней рисуется отказ.
  const versions = usePagedLoader<FormHistoryItem>(
    (params, signal) => cabinetApi.formHistory(params, signal),
    [],
    true,
    'hub.history',
  );

  const state = useLoader<HubData>(
    async () => {
      // Отметка о прочтении ключуется по аккаунту, поэтому счётчик приезжает
      // из настроек аккаунта, а не из ответа кабинета.
      const [me, account] = await Promise.all([cabinetApi.character(), cabinetApi.settings()]);
      return { me, unreadDecisions: account.unreadDecisions };
    },
    [],
    true,
    'hub',
  );

  // Лента решений отмечает прочтение, не дожидаясь ответа: вернувшись раньше
  // него, хаб прочёл бы прежнее число.
  useCountersChanged(state.reload);

  const createCharacter = async () => {
    setBusy(true);
    try {
      await cabinetApi.createCharacter();
      state.reload();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const submitForm = async () => {
    setBusy(true);
    try {
      await cabinetApi.submitForm();
      toast(t('character.toast.submitted'));
      state.reload();
      versions.reload();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  // До конца перечитывания на экране прежнее состояние: второе нажатие ушло бы по нему.
  const locked = busy || state.loading;

  // Номер досье у правого края: по нему персонажа зовут в админке и в журнале решений.
  return (
    <Screen
      id={id}
      title={t('character.title.hub')}
      number={state.data?.me.character?.id ?? state.data?.me.archived?.id}
    >
      {state.loading && state.data === null ? <LoadingState /> : null}

      {state.error !== null && state.data === null ? (
        <ErrorState
          title={t('character.error.loadFailed')}
          message={state.error}
          onRetry={state.reload}
        />
      ) : null}

      {state.data !== null ? (
        <>
          {/* Не пришло перечитывание: на экране прежнее, в том числе
              запомненное с прошлого захода, и молчать об этом нельзя. */}
          {state.error !== null ? (
            <NoticeBlock
              tone="error"
              title={t('character.error.loadFailed')}
              action={
                <Action tone="quiet" size="s" onClick={state.reload}>
                  {t('common.action.retry')}
                </Action>
              }
            >
              {state.error}
            </NoticeBlock>
          ) : null}

          <FormStatusGroup
            data={state.data}
            history={versions.data?.items ?? []}
            busy={locked}
            onOpenDiff={() => routeNavigator.push('/me/form/diff')}
            onCreateCharacter={() => void createCharacter()}
            onSubmit={() => void submitForm()}
            onOpenEditor={() => routeNavigator.push('/me/form')}
            onOpenHistory={() => routeNavigator.push('/me/form/history')}
            onOpenCard={(characterId) => routeNavigator.push(`/character/${characterId}`)}
          />

          {state.data.me.character !== null ? (
            <CabinetMenu
              unreadDecisions={state.data.unreadDecisions}
              startingAbilities={state.data.me.published?.abilities.length ?? 0}
              onNavigate={(path) => routeNavigator.push(path)}
            />
          ) : null}
        </>
      ) : null}
    </Screen>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Статус анкеты
   ────────────────────────────────────────────────────────────────────────── */

interface StatusProps {
  data: HubData;
  history: FormHistoryItem[];
  busy: boolean;
  onOpenDiff: () => void;
  onCreateCharacter: () => void;
  onSubmit: () => void;
  onOpenEditor: () => void;
  onOpenHistory: () => void;
  onOpenCard: (characterId: number) => void;
}

function FormStatusGroup({
  data,
  history,
  busy,
  onOpenDiff,
  onCreateCharacter,
  onSubmit,
  onOpenEditor,
  onOpenHistory,
  onOpenCard,
}: StatusProps) {
  const { me } = data;

  // Кнопки «завести» нет намеренно: архивный занимает слот, и сервер ответил бы 409.
  if (me.archived !== null) {
    return (
      <Section>
        <CardList>
          <Card tone="archived" padded={false}>
            <CardBanner icon={<WarningIcon />}>{t('common.state.archived')}</CardBanner>
            <CardBody>
              <CardHeading>{t('character.status.archived.title')}</CardHeading>
              <Gap step="s2h" />
              <Prose>{t('character.status.archived.text')}</Prose>
              <Gap step="s2h" />
              <Meta>
                {[t('character.meta.archivedAt', { date: formatDate(me.archived.deletedAt) })]}
              </Meta>
            </CardBody>
          </Card>
        </CardList>
      </Section>
    );
  }

  if (me.character === null) {
    return (
      <Section>
        <CardList>
          <StatusCard title={t('character.status.none.title')}>
            <Prose>{t('character.status.none.text')}</Prose>
            <Gap step="s4" />
            <Action
              tone="action"
              size="l"
              stretched
              loading={busy}
              disabled={busy}
              onClick={onCreateCharacter}
            >
              {t('character.action.create')}
            </Action>
          </StatusCard>
        </CardList>
      </Section>
    );
  }

  const character = me.character;
  // История отсортирована по номеру версии сверху вниз: свежая — первая.
  const latest = history[0] ?? null;
  const rejection =
    latest !== null && latest.status === 'rejected'
      ? (latest.decisions.find((decision) => decision.verdict === 'reject') ?? null)
      : null;

  const published = me.published;
  const open = me.open;

  /**
   * Полоса действий над анкетой — ЗНАЧКАМИ, и одна на все состояния.
   *
   * Подписями она не складывалась: «править заявку», «что изменилось» и «в
   * реестр» длиннее своей кнопки, каждая ломалась на две строки, и ряд
   * читался нагромождением слов вместо четырёх равных команд. Подписи никуда
   * не делись — они остались именем кнопки для скринридера и всплывающей
   * подсказки, видимой их просто не рисуем.
   *
   * Набор считается состоянием, а не переписывается в каждой ветке: правка
   * есть всегда, сравнение — когда поверх принятой версии лежит правка,
   * реестр — когда анкета И ПРАВДА в реестре, история — всегда.
   */
  const formActions = (options: { edit: string; diff?: boolean; registry?: boolean }) => (
    <ActionRow>
      <Action
        tone="accent"
        iconOnly
        title={options.edit}
        before={<PencilIcon />}
        onClick={onOpenEditor}
      >
        {options.edit}
      </Action>
      {options.diff === true ? (
        <Action
          tone="quiet"
          iconOnly
          title={t('character.diff.title')}
          before={<DiffIcon />}
          onClick={onOpenDiff}
        >
          {t('character.diff.title')}
        </Action>
      ) : null}
      {options.registry === true ? (
        <Action
          tone="quiet"
          iconOnly
          title={t('character.action.openCard')}
          before={<EyeIcon />}
          onClick={() => onOpenCard(character.id)}
        >
          {t('character.action.openCard')}
        </Action>
      ) : null}
      <Action
        tone="quiet"
        iconOnly
        title={t('character.section.history')}
        before={<HistoryIcon />}
        onClick={onOpenHistory}
      >
        {t('character.section.history')}
      </Action>
    </ActionRow>
  );

  return (
    <Section>
      {/* Подпись раздела, а не строка внутри карточки: «Анкета» стоит в одном
          ряду с «Имуществом», и экран читается подписями подряд, а не одной
          снаружи и одной внутри. */}
      <SectionTitle>{t('common.section.form')}</SectionTitle>
      <CardList>
        {/* Отказ показываем первым делом: это единственное состояние,
            где от игрока прямо сейчас чего-то ждут. */}
        {rejection !== null ? (
          <StatusCard
            character={character}
            status={{ tone: 'rejected', badge: t('character.badge.rejected') }}
          >
            <Prose>{t('character.status.rejected.text')}</Prose>
            <Gap step="s2h" />
            <Meta>
              {[
                latest === null
                  ? null
                  : t('character.meta.version', { no: docNumber(latest.versionNo) }),
                t('character.meta.rejectedAt', { date: formatDate(rejection.decidedAt) }),
              ]}
            </Meta>
            <Gap step="s3" />
            <ModeratorComment text={rejection.comment} />
            {published === null ? null : (
              <>
                <Gap step="s3" />
                <Notice tone="info">
                  {t('character.notice.publishedHeldAfterReject', {
                    no: docNumber(published.versionNo),
                  })}
                </Notice>
              </>
            )}
            {/* Главный ход остаётся подписью и во всю ширину: это решение, а
                не команда в ряду равных. */}
            <Gap step="s4" />
            <Action tone="action" size="l" stretched onClick={onOpenEditor}>
              {t('character.action.fixAndResubmit')}
            </Action>
            <Gap step="s2" />
            {formActions({
              edit: t('character.action.editForm'),
              registry: published !== null,
            })}
          </StatusCard>
        ) : null}

        {rejection === null && published === null && open === null ? (
          <StatusCard character={character} title={t('character.status.empty.title')}>
            <Prose>{t('character.status.empty.text')}</Prose>
            <Gap step="s4" />
            <Action tone="action" size="l" stretched onClick={onOpenEditor}>
              {t('character.action.fill')}
            </Action>
          </StatusCard>
        ) : null}

        {rejection === null && published === null && open !== null && open.status === 'draft' ? (
          <StatusCard
            character={character}
            status={{ tone: 'archived', badge: t('character.badge.draft') }}
          >
            <Meta>
              {[
                t('character.meta.version', { no: docNumber(open.versionNo) }),
                t('character.meta.editedAt', { date: formatDate(open.updatedAt) }),
              ]}
            </Meta>
            <Gap step="s4" />
            <Action
              tone="action"
              size="l"
              stretched
              loading={busy}
              disabled={busy}
              onClick={onSubmit}
            >
              {t('character.action.submit')}
            </Action>
            <Gap step="s2" />
            {formActions({ edit: t('character.action.continueForm') })}
          </StatusCard>
        ) : null}

        {rejection === null && published === null && open !== null && open.status === 'pending' ? (
          <StatusCard
            character={character}
            status={{ tone: 'pending', badge: t('common.state.pending') }}
          >
            <Prose>{t('character.status.pending.text')}</Prose>
            <Gap step="s2h" />
            <Meta>
              {[
                t('character.meta.version', { no: docNumber(open.versionNo) }),
                t('character.meta.submittedAt', { date: formatDate(open.submittedAt) }),
              ]}
            </Meta>
            <Gap step="s4" />
            {formActions({ edit: t('character.action.editRequest') })}
          </StatusCard>
        ) : null}

        {rejection === null && published !== null && open === null ? (
          <StatusCard
            character={character}
            status={{ tone: 'published', badge: t('character.badge.approved') }}
          >
            <Prose>{t('character.status.published.text')}</Prose>
            <Gap step="s2h" />
            <Meta>{[t('character.meta.version', { no: docNumber(published.versionNo) })]}</Meta>
            <Gap step="s4" />
            {formActions({ edit: t('character.action.editForm'), registry: true })}
          </StatusCard>
        ) : null}

        {rejection === null && published !== null && open !== null ? (
          <StatusCard
            character={character}
            status={
              open.status === 'pending'
                ? { tone: 'pending', badge: t('character.badge.editInFlight') }
                : { tone: 'archived', badge: t('character.badge.draft') }
            }
          >
            <Prose>
              {open.status === 'pending'
                ? t('character.status.both.pending')
                : t('character.status.both.draft')}
            </Prose>
            <Gap step="s2h" />
            <Meta>
              {[
                t('character.meta.version', { no: docNumber(open.versionNo) }),
                open.status === 'pending'
                  ? t('character.meta.submittedAt', { date: formatDate(open.submittedAt) })
                  : t('character.meta.editedAt', { date: formatDate(open.updatedAt) }),
              ]}
            </Meta>
            <Gap step="s4" />
            {open.status === 'draft' ? (
              <>
                <Action
                  tone="action"
                  size="l"
                  stretched
                  loading={busy}
                  disabled={busy}
                  onClick={onSubmit}
                >
                  {t('character.action.submitEdits')}
                </Action>
                <Gap step="s2" />
              </>
            ) : null}
            {formActions({
              edit:
                open.status === 'pending'
                  ? t('character.action.editRequest')
                  : t('character.action.continueEdits'),
              diff: true,
              registry: true,
            })}
          </StatusCard>
        ) : null}
      </CardList>
    </Section>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Остальные разделы кабинета
   ────────────────────────────────────────────────────────────────────────── */

function CabinetMenu({
  unreadDecisions,
  startingAbilities,
  onNavigate,
}: {
  unreadDecisions: number;
  /** Сколько способностей заявлено девятым пунктом принятой анкеты. */
  startingAbilities: number;
  onNavigate: (path: string) => void;
}) {
  return (
    <Section>
      {/* Свой раздел, а не подпись внутри чужого: анкета и имущество — две
          разные вещи. Слеплены в одну секцию они были, пока шов считался
          неверно и над «Имуществом» набегало сорок; теперь шов держит сам
          раздел, и складывать их незачем. */}
      <SectionTitle>{t('character.section.property')}</SectionTitle>

      <NavList>
        {/* Подписи — дословно из макета: они называют, ЧТО внутри раздела, а
            не пересказывают правила проверки. Правила написаны на самих
            экранах, и в хабе повторять их незачем. */}
        <NavCard
          title={t('common.section.inventory')}
          hint={t('character.nav.inventory.hint')}
          icon={<BoxIcon />}
          onOpen={() => onNavigate('/me/inventory')}
        />
        <NavCard
          title={t('common.section.pets')}
          hint={t('character.nav.pets.hint')}
          icon={<PawIcon />}
          onOpen={() => onNavigate('/me/pets')}
        />
        <NavCard
          title={t('character.nav.abilities.title')}
          // Счёт стартовых, как в макете: их правят в анкете, а не здесь, и
          // знать их число до перехода полезнее любой общей фразы.
          hint={
            startingAbilities > 0
              ? t('character.nav.abilities.hintCount', {
                  value: t('plural.startingAbility', { count: startingAbilities }),
                })
              : t('character.nav.abilities.hint')
          }
          icon={<BoltIcon />}
          onOpen={() => onNavigate('/me/abilities')}
        />
      </NavList>

      {/* Решения — не имущество: они про все четыре очереди сразу, поэтому
          стоят отдельной карточкой, а не четвёртой строкой раздела. */}
      <CardList>
        <NavCard
          title={t('character.nav.decisions.title')}
          hint={t('character.nav.decisions.hint')}
          icon={<VerdictIcon tone="pink" />}
          tone="rejected"
          badgeTone="rejected"
          badge={
            unreadDecisions > 0 ? `${t('plural.unreadDecision', { count: unreadDecisions })}` : null
          }
          onOpen={() => onNavigate('/me/decisions')}
        />
      </CardList>
    </Section>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Мелочи оформления
   ────────────────────────────────────────────────────────────────────────── */

function StatusCard({
  character,
  status,
  title,
  children,
}: {
  /** Что о персонаже решил модератор. Персонажа ещё нет — строки тоже нет. */
  character?: CharacterRow;
  /** Значка нет там, где нет и состояния: ни персонажа, ни анкеты. */
  status?: { tone: StatusTone; badge: string };
  /** Заголовок вместо слова «Анкета»: там, где анкеты ещё нет. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <Card tone={status === undefined ? 'default' : CARD_TONE[status.tone]}>
      {character === undefined ? null : (
        <>
          {/* Состояние анкеты стоит В РЯДУ МЕТОК, прижатым вправо: слева то,
              что за персонаж, справа то, что с его анкетой. Своей строкой со
              словом «АНКЕТА» оно занимало в карточке целый ярус, а само слово
              теперь подписывает раздел снаружи — как «Имущество». */}
          <CharacterFacts
            character={character}
            badge={
              status === undefined ? undefined : (
                <StatusBadge tone={status.tone}>{status.badge}</StatusBadge>
              )
            }
          />
          <Gap step="s3" />
          {/* Гаснущая черта, а не вкуишный Separator: сплошная линия делит
              карточку надвое, а здесь она лишь отбивает шапку от анкеты. */}
          <Divider />
          <Gap step="s3" />
        </>
      )}
      {/* Заголовок остаётся только там, где состояния нет и знак не о чем
          рисовать: «Персонажа ещё нет», «Анкеты пока нет». */}
      {title === undefined ? null : (
        <>
          <CardHeading>{title}</CardHeading>
          <Gap step="s2h" />
        </>
      )}
      {children}
    </Card>
  );
}

/**
 * Общежитие, класс и вид персонажа ставит модератор: править их негде.
 *
 * Метками, а не строкой фактов через точку: ровно так они набраны в досье
 * реестра, и разница в форме означала бы, что это разные вещи. Общежитие
 * залито — это принадлежность, класс обведён малиновым — это ранг; размер
 * обычный, потому что ряд стоит ВНУТРИ карточки, а не в шапке экрана.
 */
function CharacterFacts({ character, badge }: { character: CharacterRow; badge?: ReactNode }) {
  return (
    <ChipRow trailing={badge}>
      {/* Метка, а не знак: состояний у персонажа не бывает. */}
      <Tag>{CHARACTER_KIND_LABELS[character.kind]}</Tag>
      {/* Неназначенное общежитие остаётся на виду нейтральной меткой: игрок
          ждёт решения модератора, и пропасть эта строка не должна. */}
      {character.dorm === null ? (
        <Tag>{t('character.facts.noDorm')}</Tag>
      ) : (
        <Tag tone="success" fill>
          {DORM_LABELS[character.dorm]}
        </Tag>
      )}
      {character.studyClass === null ? null : (
        <Tag tone="error">
          {t('character.facts.studyClass', { value: STUDY_CLASS_LABELS[character.studyClass] })}
        </Tag>
      )}
    </ChipRow>
  );
}

/**
 * Отзыв модератора. Плашкой с малиновой засечкой, а не подписью и абзацем под
 * ней: в макете чужой текст внутри карточки всегда отбит цветной чертой —
 * иначе он читается как продолжение нашего собственного пояснения.
 */
function ModeratorComment({ text }: { text: string | null }) {
  // Заголовка нет: карточка выше уже сказала «отклонена», и «Комментарий
  // модератора» над самим комментарием повторяет то же слово дважды. В макете
  // чужой текст стоит просто отбитым цветной чертой.
  return <Notice tone="error">{text ?? t('character.moderator.noComment')}</Notice>;
}
